import { useContext, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';

import { Container } from '@/components/ui/flexbox';

import { Button, sword } from '@swordhealth/ui-corporate';
import { products } from '@/utils/products';
import ClientContext from '@/contexts/client.context';
import { ACTIONS, BUTTON_TYPES, trackButtonClicked } from '@/utils/track';
import { useInView } from 'react-intersection-observer';
import useBetterMediaQuery from '@/utils/useBetterMediaQuery';
import Image from 'next/image';
import { Logos } from '@/app/_components/shared/Logos/Logos';
import { Buttons, Wrapper, WrapperInner } from './styles';

const Bar = ({
  clientKey,
  isOrganicLP,
  logos,
  navigation,
  params,
  portugueseHealthPlan = false,
  selectProduct,
  showSwordLogo,
}) => {
  const { setProduct } = useContext(ClientContext);
  const isMobile = useBetterMediaQuery(`(max-width: ${sword.breakpoints.maxSm})`);
  const searchParams = useSearchParams();

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: isMobile ? '30px' : '0px',
  });

  const shouldRenderButtons = useMemo(
    () => !clientKey || (!isMobile && clientKey),
    [clientKey, isMobile],
  );

  const handleLoginClick = () => {
    const destination = params
      ? `${process.env.NEXT_PUBLIC_ONBOARDING_BASE_URL + '?' + params}`
      : `${process.env.NEXT_PUBLIC_ONBOARDING_BASE_URL}`;

    trackButtonClicked({
      action: ACTIONS.REDIRECT_TO_SIGN_IN,
      buttonLocation: 'header',
      buttonText: 'Sign in here',
      buttonType: BUTTON_TYPES.NAVIGATION,
    });

    window.location.assign(destination);
  };

  const handleGetStartedClick = () => {
    const urlparams = new URLSearchParams(searchParams?.toString());
    if (clientKey) urlparams.append('client', clientKey);

    const destination = `/eligibility-check/?${urlparams}`;

    trackButtonClicked({
      action: portugueseHealthPlan ? ACTIONS.REDIRECT_TO_FORM : ACTIONS.REDIRECT_TO_COVERAGE,
      buttonType: BUTTON_TYPES.NAVIGATION,
      buttonText: 'Get started',
      destination,
      buttonLocation: 'bar',
    });

    window.open(destination, '_blank', 'noopener,noreferrer');

    if (!portugueseHealthPlan) {
      selectProduct(products.PLATFORM);
      setProduct(products.PLATFORM);
    }
  };

  return (
    <div ref={ref}>
      <Wrapper
        $white={!inView}
        $notSticky={portugueseHealthPlan && !navigation?.buttonLabel}
        $referralMultiprogram
      >
        <Container>
          <WrapperInner $extraPadding={portugueseHealthPlan}>
            {!logos && (
              <Image
                loading="lazy"
                alt="Sword logo"
                src="/sword__colored.svg"
                height={isMobile ? 24 : 28}
                width={isMobile ? 100 : 116}
              />
            )}

            {logos && (
              <Logos
                logos={logos}
                noPadding
                centered={isMobile && logos?.length >= 1 && showSwordLogo}
                customHeight={isMobile ? 22 : 32}
                showSwordLogo={showSwordLogo}
              />
            )}

            {shouldRenderButtons && (
              <Buttons>
                {!portugueseHealthPlan && (
                  <Button variant="secondary" size="sm" onClick={handleLoginClick}>
                    Login
                  </Button>
                )}

                {(isOrganicLP || navigation?.buttonLabel) && (
                  <Button variant="primary" size="sm" onClick={handleGetStartedClick}>
                    {navigation?.buttonLabel || 'Get started'}
                  </Button>
                )}
              </Buttons>
            )}
          </WrapperInner>
        </Container>
      </Wrapper>
    </div>
  );
};

export default Bar;
